import React from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import ActiveIcon from '@/assets/icons/active.svg';
import DisabledIcon from '@/assets/icons/disabled.svg';
import { COLORS, BREAKPOINTS, FONT_SIZE, GLOBAL, SPACE } from '@/constants';
import { shouldForwardProp } from '@/utils/should-forward-prop';
import { type LinkSet } from '@/constants/links';
import { logTrackingEvent } from '@/utils/tracking';

const StyledNavTitle = styled.div`
  font-size: ${FONT_SIZE.LARGE};
  font-family: ${GLOBAL.BRAND_FONT_FAMILY_SECOND};
  margin-bottom: ${SPACE.X2};
  line-height: 26px;
`;

const StyledNavLinks = styled.ul`
  display: flex;
  flex-direction: column;
  list-style-type: none;
  margin: 0;
  padding-left: 0;

  @media (min-width: ${BREAKPOINTS.MD}px) {
    justify-content: flex-end;
    flex-direction: row;
  }
`;

type StyledLinkProps = {
  isSecondary?: boolean;
  isActive?: boolean;
};

const StyledLinkWrapper = styled('li', { shouldForwardProp })<StyledLinkProps>`
  user-select: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: transparent; /* For some Androids */

  margin-bottom: 0;

  @media (min-width: ${BREAKPOINTS.MD}px) {
    margin-right: ${SPACE.X1};
  }

  @media (min-width: ${BREAKPOINTS.LG}px) {
    margin-right: calc(${SPACE.X1} + ${SPACE.X0});
  }
`;

export const StyledLink = styled('a', { shouldForwardProp })<StyledLinkProps>`
  color: ${COLORS.GREY_ELEVEN};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-family: ${GLOBAL.BRAND_FONT_FAMILY};

  font-size: ${({ isSecondary }) => (isSecondary ? `${FONT_SIZE.XX_LARGE}` : `${FONT_SIZE.XXX_LARGE}`)};
  transition: 0.1s ease-in all;
  user-select: none;
  width: 100%;

  ::after {
    content: url(${({ isActive }) => (isActive ? DisabledIcon : ActiveIcon)});
    margin-left: auto;
    padding-bottom: ${SPACE.X1};
  }

  @media (min-width: ${BREAKPOINTS.MD}px) {
    font-family: ${GLOBAL.BRAND_FONT_FAMILY_SECOND};
    font-size: ${FONT_SIZE.LARGE};
    ::after {
      content: none;
    }
  }

  @media (min-width: ${BREAKPOINTS.LG}px) {
    font-size: ${FONT_SIZE.X_LARGE};
  }
`;

const StyledLinkTitle = styled('span', { shouldForwardProp })<Pick<StyledLinkProps, 'isActive'>>`
  border-bottom: ${(props) => `4px solid ${props.isActive ? COLORS.BRAND_BLUE_SIX : 'transparent'} `};
  line-height: ${GLOBAL.HEADER_HEIGHT[0]};

  @media (min-width: ${BREAKPOINTS.MD}px) {
    line-height: ${GLOBAL.HEADER_HEIGHT[1]};
  }

  @media (min-width: ${BREAKPOINTS.LG}px) {
    line-height: ${GLOBAL.HEADER_HEIGHT[2]};
  }
`;

export type NavigationLink = {
  title: string;
  url: string;
};

export type NavLinksProps = {
  navigationProps: {
    links: NavigationLink[] | LinkSet;
    navTitle?: string | React.JSX.Element | React.ReactNode;
  };

  isSecondary?: boolean;
};

export const NavLinks = ({ navigationProps, isSecondary = false }: NavLinksProps) => {
  const { links, navTitle } = navigationProps;

  const router = useRouter();
  const getIsActive = (url: string) => router.pathname === url;

  return (
    <>
      {navTitle && <StyledNavTitle>{navTitle}</StyledNavTitle>}
      <StyledNavLinks>
        {links.map(({ title, url }) => (
          <StyledLinkWrapper key={title} isSecondary={isSecondary} isActive={getIsActive(url)}>
            <StyledLink
              href={url}
              isSecondary={isSecondary}
              isActive={getIsActive(url)}
              onClick={() =>
                logTrackingEvent({
                  event: 'navigation_click',
                  navigation_type: 'main_nav',
                  click_text: title,
                  click_url: url,
                  nav_level: '1',
                })
              }
            >
              <StyledLinkTitle isActive={getIsActive(url)}>{title}</StyledLinkTitle>
            </StyledLink>
          </StyledLinkWrapper>
        ))}
      </StyledNavLinks>
    </>
  );
};

export default NavLinks;
